import { ref, computed, watch } from 'vue';
import type { NoticeListItem } from '@/entities/ReminderHistory';

export function useReminderHistorySelection(
  notices: () => NoticeListItem[],
  onUpdate?: (selectedIds: number[]) => void
) {
  const selectedNotices = ref<Set<number>>(new Set());

  const isSelected = (id: number) => selectedNotices.value.has(id);

  const toggleSelection = (id: number) => {
    if (selectedNotices.value.has(id)) {
      selectedNotices.value.delete(id);
    } else {
      selectedNotices.value.add(id);
    }
  };

  const toggleAll = () => {
    const allCurrentlySelected = notices().every((n) => isSelected(n.id));
    if (allCurrentlySelected) {
      selectedNotices.value.clear();
    } else {
      notices().forEach((n) => selectedNotices.value.add(n.id));
    }
  };

  const selectedCount = computed(() => selectedNotices.value.size);
  const hasSelection = computed(() => selectedCount.value > 0);
  const selectedIds = computed(() => Array.from(selectedNotices.value));

  const allSelected = computed(
    () => notices().length > 0 && notices().every((n) => isSelected(n.id))
  );

  watch(
    selectedIds,
    (ids) => {
      onUpdate?.(ids);
    },
    { deep: true }
  );

  const removeFromSelection = (ids: number[]) => {
    ids.forEach((id) => selectedNotices.value.delete(id));
  };

  const clearSelection = () => {
    selectedNotices.value.clear();
  };

  return {
    isSelected,
    toggleSelection,
    toggleAll,
    selectedCount,
    hasSelection,
    selectedIds,
    allSelected,
    removeFromSelection,
    clearSelection,
  };
}
